<template>
  <div>
    <vx-card>
      <p class="mb-5">
        Information in this section (Introduction) does not need to be learned or memorised and you
        will not be quizzed on it.
      </p>
      <p>
        This information is provided for you to read through and gain an understanding of this
        particular career path before starting your Virtual Job Experience.
      </p>
    </vx-card>
    <vx-card class="mt-base mb-10">
      <h4 class="mb-5">Let’s start your virtual job experience in the Veterinary Industry!</h4>
      <p class="mb-5">
        Working as a vet or vet nurse is a challenging job that makes high demands on an
        individual’s technical and interpersonal skills.
      </p>
      <p class="mb-5">
        Not only that, but, often, the animals you’ll be working with will be frightened or in pain,
        putting you at risk of being kicked, scratched, or bitten.
      </p>
      <p class="mb-5">
        Just like doctors and nurses who work with humans, there’ll be times when you may need to
        work late nights, over weekends, and during holiday periods responding to emergencies or
        urgent appointments.
      </p>
      <p class="mb-5">But, it isn’t all bad. Not by a long shot!</p>
      <p class="mb-5">
        Many of those who end up becoming vets or vet nurses have a deep love for animals. Taking
        care of animals in careers like these can be hugely rewarding personally in a way that is
        impossible to match for most other occupations.
      </p>
      <p class="mb-5">
        You may also find yourself working in a wide range of environments throughout your career.
        Vets and vet nurses can find themselves working anywhere from private practice clinics,
        farms, stations, zoo’s, public health offices, and research laboratories.
      </p>
      <p class="mb-5">
        You can use the tabs below to access specific information on becoming a Veterinarian or a
        Vet Nurse.
      </p>
    </vx-card>

    <h4 class="mb-5">Description:</h4>
    <p class="mb-5">Here is some basic information you’ll need to know about these two careers.</p>

    <vs-tabs alignment="fixed" v-model="activeTab" class="mb-5 no-padding overwrite-tabs">
      <vs-tab label="Veterinarian (Vet)">
        <div class="py-2">
          <vx-card class="no-shadow">
            <p class="mb-5">
              The primary purpose of vets is to treat disease and dysfunction in animals. This
              includes household pets and other companion animals, livestock, animals in
              laboratories and zoos, and sometimes animals in the wild.
            </p>
            <img
              src="@/assets/images/experiences/vet/vet_introduction.jpg"
              alt=""
              class="my-2 mb-2 float-right"
            />

            <p class="mb-5">
              Some vets also conduct clinical research into diseases carried by animals, while
              others work to develop medicines. Vets sometimes make contributions to the development
              of medications and treatments that benefit humans too. For example, vets played an
              essential role in helping to prevent and treat yellow fever and malaria, among other
              diseases dangerous to humans.
            </p>
            <p class="mb-5">
              Most vets in Australia work in private practices that tend to pets. As well as
              diagnosing and treating diseases, they set fractures, perform surgeries, and advise
              owners on various aspects of looking after their pets.
            </p>
            <p class="mb-5">
              Those vets who work with livestock, spend time traveling to stations and farms to
              treat individual animals or herds. As with those vets who work with pets, vets working
              with livestock vaccinate, treat and dress wounds, and consult with owners regarding
              animal care issues. They also can often be found helping animals to give birth to
              their young.
            </p>
            <p class="mb-5">
              Working as a vet doesn’t just involve working with animals, though. A large part of
              their job description involves building relationships with the animal owners, which
              requires interpersonal skills. Frequently, they’ll run their own business, which
              requires an entirely different set of skills.
            </p>
            <p class="mb-5">There’s no doubt about it; being a vet is a multi-faceted career!</p>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Vet Nurse">
        <div class="py-2">
          <vx-card class="no-shadow">
            <p class="mb-5">
              Vet nurses are crucial members of any veterinary team. Working closely with vets, they
              provide care and treatment to a wide range of animals.
            </p>
            <img
              src="@/assets/images/experiences/vet/vet_nurse_introduction.jpg"
              alt=""
              class="my-2 mb-2 float-right"
            />

            <p class="mb-5">
              The job description of a vet nurse is varied - to say the least! Some tasks a vet
              nurse will be expected to perform include:
            </p>
            <ul class="default-list">
              <li>Preparing an animal for surgery</li>
              <li>Assisting the vet in the operating theatre</li>
              <li>Administering medication</li>
              <li>Nursing sick animals</li>
              <li>Taking bookings and payments</li>
              <li>Running nurse clinics on topics related to animal care</li>
            </ul>
            <p class="mb-5">
              Sometimes you’ll need to muck-in too. There may be floors to mop and cages and kennels
              to clean. One thing’s for sure; there’ll never be a dull moment!
            </p>
          </vx-card>
        </div>
      </vs-tab>
    </vs-tabs>

    <h4 class="mb-5">Education Level:</h4>
    <p class="mb-5">
      The levels of education you will need to get into these two careers are quite different. Here
      is a summary of both.
    </p>

    <vs-tabs alignment="fixed" v-model="activeTab" class="mb-5 no-padding overwrite-tabs">
      <vs-tab label="Veterinarian (Vet)">
        <div class="py-2">
          <vx-card class="no-shadow">
            <p class="mb-5">
              You need a bachelor’s degree in veterinary science to work as a Veterinarian.
            </p>
            <p class="mb-5">Registration or licencing is required.</p>
            <p class="mb-5">
              Before starting a course, check it will provide you with the skills and qualifications
              you need. Visit
            </p>
            <p class="mb-5">
              <a href="https://www.courseseeker.edu.au/" target="_blank">Course Seeker</a> to search
              and compare higher education courses.
            </p>
            <p class="mb-5">
              <a href="https://www.compared.edu.au/" target="_blank">ComparED</a> to compare
              undergraduate and postgraduate student experiences and outcomes.
            </p>
            <h4 class="mb-5">Useful Links and Resources</h4>
            <ul class="default-list">
              <li>
                <a href="https://www.ava.com.au/" target="_blank"
                  >Australian Veterinary Association</a
                >
              </li>
              <li>
                <a href="https://avbc.asn.au/" target="_blank"
                  >The Australasian Veterinary Boards Council Incorporated (AVBC Inc.)</a
                >
              </li>
              <li>
                <a href="https://www.vpb.nsw.gov.au/" target="_blank"
                  >Veterinary Practitioners Board of New South Wales</a
                >
              </li>
              <li>
                <a href="https://www.vetboard.vic.gov.au/" target="_blank"
                  >The Veterinary Practitioners Registration Board of Victoria</a
                >
              </li>
              <li>
                <a href="https://www.vsb.qld.gov.au/" target="_blank"
                  >Veterinary Surgeons Board of Queensland</a
                >
              </li>
              <li>
                <a href="http://www.vsbsa.org.au/" target="_blank"
                  >Veterinary Surgeons Board of South Australia</a
                >
              </li>
              <li>
                <a href="https://www.vsbwa.org.au/" target="_blank"
                  >Veterinary Surgeons Board of Western Australia</a
                >
              </li>
              <li>
                <a
                  href="https://dpipwe.tas.gov.au/biosecurity-tasmania/animal-biosecurity/veterinary-board-of-tasmania"
                  target="_blank"
                  >Veterinary Board of Tasmania</a
                >
              </li>
              <li>
                <a
                  href="https://dpir.nt.gov.au/primary-industry/committees-and-boards/veterinary-board-of-the-NT"
                  target="_blank"
                  >Veterinary Board of the Northern Territory</a
                >
              </li>
              <li>
                <a
                  href="https://www.cityservices.act.gov.au/pets-and-wildlife/veterinary-practitioners-board"
                  target="_blank"
                  >Veterinary Practitioners Board of the ACT</a
                >
              </li>
            </ul>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Vet Nurse">
        <div class="py-2">
          <vx-card class="no-shadow">
            <p class="mb-5">
              You usually need a certificate III or IV in veterinary nursing to work as a Veterinary
              Nurse. Some workers complete a traineeship.
            </p>
            <p class="mb-5">Registration or licencing may be required.</p>
            <p class="mb-5">
              Before starting a course, check it will provide you with the skills and qualifications
              you need. Visit
            </p>
            <p class="mb-5">
              <a href="https://www.myskills.gov.au/" target="_blank">My Skills</a> to compare
              Vocational Education and Training (VET) courses, providers, and student outcomes.
            </p>
            <p class="mb-5">
              <a href="https://www.aapathways.com.au/career-research/job-pathways" target="_blank"
                >AAPathways website</a
              >
              to explore Animal Care and Management VET training pathways.
            </p>
            <h4 class="mb-5">Useful Links and Resources</h4>
            <ul class="default-list">
              <li>
                <a href="https://www.vnca.asn.au/" target="_blank"
                  >Veterinary Nurses Council of Australia</a
                >
              </li>
              <li>
                <a href="https://piaa.net.au/" target="_blank"
                  >Pet Industry Association of Australia</a
                >
              </li>
            </ul>
          </vx-card>
        </div>
      </vs-tab>
    </vs-tabs>

    <h4 class="mb-5">Quick Details:</h4>
    <p class="mb-5">
      Here’s everything you could want to know about money/earnings, work hours and what the job
      prospects are.
    </p>

    <vs-tabs alignment="fixed" v-model="activeTab" class="mb-5 no-padding overwrite-tabs">
      <vs-tab label="Veterinarian (Vet)">
        <div class="py-2">
          <vx-card class="no-shadow">
            <div class="vx-row mt-5 flex justify-center">
              <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
                <p class="mb-5"><strong>Size:</strong> This is a medium-sized occupation.</p>
                <p class="mb-5">
                  <strong>Earnings:</strong> Full-time workers on an adult wage earn around $1,384
                  per week (similar to the average of $1,460). Earnings tend to be lower when
                  starting out and higher as experience grows.
                </p>
                <p class="mb-5">
                  <strong>Full-time:</strong> Many work full-time (72%, higher than the average of
                  66%).
                </p>
                <p class="mb-5">
                  <strong>Hours:</strong> Full-time workers spend around 46 hours per week at work
                  (compared to the average of 44 hours).
                </p>
                <p class="mb-5">
                  <strong>Age:</strong> The average age is 39 years (compared to the average of 40
                  years).
                </p>
                <p class="mb-5">
                  <strong>Gender:</strong> 61% of workers are female (compared to the average of
                  48%).
                </p>

                <h4 class="mb-5">Tasks:</h4>
                <ul class="default-list">
                  <li>
                    Treating animals medically and surgically, and administering and prescribing
                    drugs, analgesics, and general and local anaesthetics
                  </li>
                  <li>
                    Determining the presence and nature of abnormal conditions by physical
                    examination, laboratory testing, and through diagnostic imaging techniques,
                    including radiography and ultrasound
                  </li>
                  <li>Performing surgery, dressing wounds, and setting broken bones</li>
                  <li>Rendering obstetric services to animals</li>
                  <li>
                    Participating in programs designed to prevent the occurrence and spread of
                    animal diseases
                  </li>
                  <li>
                    Inoculating animals against, and testing for, infectious diseases and notifying
                    authorities of outbreaks of infectious animal diseases
                  </li>
                  <li>Performing autopsies to determine the cause of death</li>
                  <li>
                    Advising clients on health, nutrition and feeding, hygiene, breeding, and care
                    of animals
                  </li>
                  <li>
                    May provide professional services to commercial firms producing biological and
                    pharmaceutical products
                  </li>
                  <li>
                    May specialise in the treatment of a particular animal group or in a particular
                    specialty area such as cardiology, chiropractic, dermatology, or critical care.
                  </li>
                </ul>
                <p class="mb-5">
                  [Source:
                  <a href="https://joboutlook.gov.au" target="_blank">https://joboutlook.gov.au</a>]
                </p>
              </div>
              <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
                <img
                  src="@/assets/images/experiences/vet/jo1.png"
                  class="mx-auto img-responsive"
                  alt="Veterinarians job outlook: $1,384 weekly pay, very strong future growth, 9,100 workers, very high skill level, 72% full-time, 46 hours average full-time week, average age 39, 61% female."
                />
              </div>
            </div>
            <vs-alert color="dark" title="Caution" active="true">
              The Australian jobs market is changing in response to the COVID-19 pandemic. These
              estimates do not take account of the impact of COVID-19. They may not reflect the
              current jobs market and should be used and interpreted with extreme caution.
            </vs-alert>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="Vet Nurse">
        <div class="py-2">
          <vx-card class="no-shadow">
            <div class="vx-row mt-5 flex justify-center">
              <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
                <p class="mb-5"><strong>Size:</strong> This is a medium-sized occupation.</p>
                <p class="mb-5">
                  <strong>Earnings:</strong> Full-time workers on an adult wage earn around $1,088
                  per week (below the average of $1,460). Earnings tend to be lower when starting
                  out and higher as experience grows.
                </p>
                <p class="mb-5">
                  <strong>Full-time:</strong> Around half work full-time (49%, less than the average
                  of 66%), showing there are many opportunities to work part-time.
                </p>
                <p class="mb-5">
                  <strong>Hours:</strong> Full-time workers spend around 40 hours per week at work
                  (compared to the average of 44 hours).
                </p>
                <p class="mb-5">
                  <strong>Age:</strong> The average age is 29 years (compared to the average of 40
                  years). Many workers are under 25 years of age (30%).
                </p>
                <p class="mb-5">
                  <strong>Gender:</strong> 97% of workers are female (compared to the average of
                  48%).
                </p>

                <h4 class="mb-5">Tasks:</h4>
                <ul class="default-list">
                  <li>Holding animals to allow examination and treatment by Veterinarians</li>
                  <li>Cleaning and sterilising examination tables and equipment</li>
                  <li>Preparing instruments and handing them to the Veterinarian</li>
                  <li>
                    Assisting Veterinarians to administer anaesthetics and oxygen during operations
                  </li>
                  <li>
                    Placing animals in cages for recovery from operations and monitoring their
                    condition
                  </li>
                  <li>Giving medications to animals</li>
                  <li>Maintaining stock control and records</li>
                  <li>
                    Providing animal care advice and preparing, delivering, and reviewing animal
                    care education programs
                  </li>
                  <li>May perform diagnostic laboratory tests</li>
                  <li>May act as a receptionist, accept payments and undertake clerical work.</li>
                </ul>
                <p class="mb-5">
                  [Source:
                  <a href="https://joboutlook.gov.au" target="_blank">https://joboutlook.gov.au</a>]
                </p>
              </div>
              <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
                <img
                  src="@/assets/images/experiences/vet/jo2.png"
                  alt="Veterinary Nurses job outlook: $1,088 weekly pay, strong future growth, 12,100 workers, medium skill level, 49% full-time, 40 hours average full-time week, average age 29, 97% female."
                  class="mx-auto img-responsive"
                />
              </div>
            </div>

            <vs-alert color="dark" title="Caution" active="true">
              The Australian jobs market is changing in response to the COVID-19 pandemic. These
              estimates do not take account of the impact of COVID-19. They may not reflect the
              current jobs market and should be used and interpreted with extreme caution.
            </vs-alert>
          </vx-card>
        </div>
      </vs-tab>
    </vs-tabs>

    <h4 class="mb-5">Some other things to think about:</h4>

    <vs-tabs alignment="fixed" v-model="activeTab" class="mb-5 no-padding overwrite-tabs">
      <vs-tab label="Benefits of becoming a Vet">
        <div class="py-2">
          <vx-card class="no-shadow">
            <vs-collapse accordion>
              <vs-collapse-item>
                <div slot="header">1. Take Charge of Your Career</div>
                <p class="mb-5">
                  As a qualified vet, you’ll have opportunities to pursue many different options. If
                  being self-employed appeals to you, you might want to start your own small clinic.
                  If that’s not your cup of tea, you might want to work for a large clinic, go into
                  public health, or teach veterinary science.
                </p>
                <p class="mb-5">
                  As you grow in skills, knowledge, and experience, even more doors will open to
                  you. In this profession, you’ll be able to pursue your own specific interests and
                  ambitions throughout your career.
                </p>
              </vs-collapse-item>
              <vs-collapse-item>
                <div slot="header">2. Job Security</div>
                <p class="mb-5">
                  The Australian government has identified this job as one of predicted Very Strong
                  Growth.
                </p>
                <p class="mb-5">
                  Not only does pet ownership increase year on year as the population grows, but so
                  do animal care standards and the demand for vets to provide the necessary care.
                  All this can only be good news for anyone thinking of becoming a vet.
                </p>
              </vs-collapse-item>
              <vs-collapse-item>
                <div slot="header">3. Build Relationships with Others</div>
                <p class="mb-5">
                  As a vet, you’ll have lots of opportunities to interact with others - and not just
                  the furry kind!
                </p>
                <p class="mb-5">
                  As well as building strong working and personal relationships with your immediate
                  colleagues, you’ll make connections with the animal owners you work with
                  regularly. If you are involved in teaching, you’ll have opportunities to work
                  closely with the next generation of vets as you mentor and guide them.
                </p>
                <p class="mb-5">
                  While everyone knows vets work with animals, it’s sometimes forgotten that vets
                  work with and help humans too. A shared interest in the welfare of animals goes a
                  long way to building relationships between like-minded people.
                </p>
              </vs-collapse-item>
              <vs-collapse-item>
                <div slot="header">4. Enjoy a Rewarding Career</div>
                <p class="mb-5">
                  This is a caring profession. Relieving the suffering of the animals in your care
                  (and their owners!) can be very rewarding.
                </p>
                <p class="mb-5">
                  Many jobs these days involve intangible things. It can be challenging to find a
                  deeper meaning. This is certainly not the case with being a vet. With each
                  surgery, routine vaccination, spaying, neutering, and even euthanisation, you
                  contribute to easing animal suffering in the world.
                </p>
              </vs-collapse-item>
            </vs-collapse>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="What makes a good Vet Nurse">
        <div class="py-2">
          <vx-card class="no-shadow">
            <vs-collapse accordion>
              <vs-collapse-item>
                <div slot="header">1. A Love of Animals</div>
                <p class="mb-5">
                  Most veterinary nurses have a deep love for animals. They are dedicated to their
                  care and often go the extra mile for their patients. Sometimes even to the point
                  that they take the animals they care for home with them, for example, when
                  abandoned kittens need temporary hand-rearing.
                </p>
                <p class="mb-5">
                  This is a caring profession, and those who wish to work in this sector need to be
                  compassionate by nature.
                </p>
              </vs-collapse-item>
              <vs-collapse-item>
                <div slot="header">2. A Team Player</div>
                <p class="mb-5">
                  Being able to work as a member of a close-knit team is vital in this role. At
                  times, you’ll be challenged to work closely with others in high-stress situations,
                  such as surgery.
                </p>
                <p class="mb-5">
                  At other times, you’ll need to take the initiative and work well on your own
                  without direction.
                </p>
              </vs-collapse-item>
              <vs-collapse-item>
                <div slot="header">3. The Ability to Cope with Pressure</div>
                <p class="mb-5">
                  While you’ll be able to help many animals in this profession, there will be others
                  you can’t. From terminally ill household pets to old and sick farm animals that
                  need to be euthanised, your resilience will be tested.
                </p>
                <p class="mb-5">
                  You’ll need to be robust to cope with the emotional and physical demands of this
                  job. You’ll need to find the right balance between being compassionate and being
                  tough enough to move on when tragedy strikes.
                </p>
              </vs-collapse-item>
              <vs-collapse-item>
                <div slot="header">4. Flexibility</div>
                <p class="mb-5">
                  While not all veterinary practices offer out-of-hours or emergency services, many
                  do. There is a good likelihood that you’ll need to work unsociable hours at some
                  point in your career.
                </p>
                <p class="mb-5">
                  Not only that, with such a varied range of tasks and duties, you’ll need to be
                  ready to do everything, including making appointments, monitoring anaesthetics,
                  medicating patients, taking x-rays, dressing wounds, maintaining equipment,
                  consulting with owners, cleaning, etc.
                </p>
              </vs-collapse-item>
              <vs-collapse-item>
                <div slot="header">5. Commitment to Ongoing Professional Development</div>
                <p class="mb-5">
                  As a vet nurse, you’ll need to be enthusiastic about developing your knowledge and
                  skills on an ongoing basis throughout your career. Things change as new
                  technologies come online and research throws up new approaches to the care and
                  treatment of animals.
                </p>
                <p class="mb-5">
                  Not only that but improving your skills can help you to grow and specialise in
                  particular areas that interest you.
                </p>
              </vs-collapse-item>
            </vs-collapse>
          </vx-card>
        </div>
      </vs-tab>
    </vs-tabs>
  </div>
</template>

<script>
export default {
  data() {
    return {
      cover_img: require('@/assets/images/other/cover.jpg'),
      popupActive: false,
      videoTrigger1: require('@/assets/images/experiences/psychology/becoming_a_registered_psychologist_trigger.jpg'),
      videos: {
        1:
          $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
          '/content/psychology/become_a_psychologist.mp4',
      },
      activeTab: 0,
    };
  },
};
</script>
